import { Card, CardContent, Tooltip, Typography } from '@mui/material';

import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import theme from '../../../../theme';
import { absChangeRatio, changeCode, formatNumber, formatPercent } from '../../HatUtils';
import { HatChangeMetricWithSkeleton } from './HatChangeMetricWithSkeleton';
import { useHatDbContext } from '../../context/HatDbContext';
import { useHatComputedMetric, useHatDbDataContext } from '../../context/HatDbDataContext';
import { HatComputedMetric } from '../../HatMetrics';
import { HelpOutlineOutlined } from '@mui/icons-material';
import { useEffect } from 'react';

// Width of skeleton placeholders in the card.
const skeletonWidth = 40;

export default function HatDbVisualComputedMetricCard(props: {
    hatMetric: HatComputedMetric;
    label: string | null | undefined | React.ReactElement;
    tooltip?: string;
    dataContextKey?: string;
}) {
    const { hatMetric, label, tooltip } = props;

    const hatDbContext = useHatDbContext();
    const hatDbDataContext = useHatDbDataContext();

    const previousCriteria = hatDbContext.getPreviousRangeCriteria();

    const currentQuery = useHatComputedMetric({
        ...hatMetric,
        interval: 'current',
    });

    const previousQuery = useHatComputedMetric(
        {
            ...hatMetric,
            interval: 'previous',
        },
        !!previousCriteria
    );

    useEffect(() => {
        // Publish the totals on the dashboard data context.
        if (props.dataContextKey) {
            hatDbDataContext?.setData(props.dataContextKey, 'current', currentQuery.data);
            hatDbDataContext?.setData(props.dataContextKey, 'previous', previousQuery.data);
        }
    }, [currentQuery.data, previousQuery.data, hatDbDataContext, props.dataContextKey]);

    return (
        <Card variant="outlined">
            <CardContent
                sx={{
                    minHeight: 128,
                    position: 'relative',
                    '&.MuiCardContent-root:last-child': {
                        pb: theme.spacing(2),
                    },
                }}
            >
                <Column justifyContent="space-between">
                    <Row gap={0.5} alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="body1" color="text.secondary">
                            {label}
                        </Typography>
                        {tooltip && (
                            <Tooltip title={tooltip}>
                                <HelpOutlineOutlined color="action" sx={{ fontSize: 16 }} />
                            </Tooltip>
                        )}
                    </Row>

                    <Column gap={1}>
                        <Typography
                            variant="h5"
                            component="div"
                            color="text.primary"
                            sx={{
                                fontSize: 24,
                            }}
                        >
                            <HatChangeMetricWithSkeleton
                                value={currentQuery.data}
                                format={formatNumber}
                                skeleton={skeletonWidth}
                            />
                        </Typography>

                        {previousCriteria && (
                            <Row gap={0.5}>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    color="text.primary"
                                    sx={{
                                        fontSize: 14,
                                    }}
                                >
                                    <HatChangeMetricWithSkeleton
                                        value={absChangeRatio(currentQuery.data, previousQuery.data)}
                                        format={formatPercent}
                                        changeCode={changeCode(currentQuery.data, previousQuery?.data)}
                                        skeleton={skeletonWidth}
                                    />
                                </Typography>
                            </Row>
                        )}
                    </Column>
                </Column>
            </CardContent>
        </Card>
    );
}
